/* eslint-disable no-restricted-syntax */
import {forwardRef, memo, useEffect, useLayoutEffect, useRef, useState} from 'react';

import Box from '@mui/material/Box';
import {useMouse} from '@uidotdev/usehooks';
import {bool} from 'prop-types';

import ChooseDroppedLinesCategoryModal from './ChooseDroppedLinesCategoryModal';
import {SNACKBAR_ACTIONS, SPREADSHEETS_IDS} from '../../../const';
import useProjects from '../../../hooks/providers/useProjects';
import useProjectsCustomMappings from '../../../hooks/providers/useProjectsCustomMappings';
import useSnackbar from '../../../hooks/providers/useSnackbar';
import {addContextMenuOption, CUSTOM_MAPPINGS_COLUMNS_NAMES as COLUMNS, CUSTOM_MAPPINGS_HEADER_ROW as HEADER_ROW, getCustomMappingsColumns} from '../../../utils';
import AutoScroll from '../../autoscroll/AutoScroll';
import BaseReactGrid from '../../spreadsheets/BaseReactGrid';
import TextCellWithDragIndicator from '../../spreadsheets/cell-templates/TextCellWithDragIndicator';
import ToggleRowVisibilityCellTemplate from '../../spreadsheets/cell-templates/ToggleVisibilityCellTemplate';

// eslint-disable-next-line react/prop-types
const Container = forwardRef(({chooseDroppedLinesCategoryModalOpen, ...props}, ref) => {
  const Component = chooseDroppedLinesCategoryModalOpen ? Box : AutoScroll;
  return (
    <div ref={ref}>
      <Component {...props} />
    </div>
  );
});

const CustomMappingsSpreadsheet = ({hiddenRowsVisible}) => {
  const [mouse, ref] = useMouse();

  const {selectedProject} = useProjects();
  const {showSnackbar} = useSnackbar();
  const {projectCustomMappings, setProjectCustomMappings: setData} = useProjectsCustomMappings();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedColumnId, setSelectedColumnId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const [chooseDroppedLinesCategoryModalOpen, setChooseDroppedLinesCategoryModalOpen] = useState(false);
  const [droppedLinesCategories, setDroppedLinesCategories] = useState([]);
  const [droppedLinesSelectedCategory, setDroppedLinesSelectedCategory] = useState(null);
  const [droppedLinesMappingOrder, setDroppedLinesMappingOrder] = useState(null);
  const [rowsIdsWaitingToBeMoved, setRowsIdsWaitingToBeMoved] = useState([]);
  const [rowsToBeMovedTargetRowId, setRowsToBeMovedTargetRowId] = useState(null);

  const gridRef = useRef();

  const {siren} = selectedProject;
  const data = projectCustomMappings.get(siren);

  const handleCellFocusChange = cellLocation => {
    setSelectedRowId(cellLocation.rowId);
    setSelectedColumnId(cellLocation.columnId);
  };

  const toggleRowVisibility = rowId => {
    setData(previousMap => {
      const previousData = previousMap.get(siren);
      const updatedData = [...previousData.map(e => ({...e}))];

      const entryBeforeUpdateIndex = previousData.findIndex(entry => entry.idx === rowId);
      const entryBeforeUpdate = previousData[entryBeforeUpdateIndex];
      const updatedEntry = {
        ...entryBeforeUpdate,
        is_visible: !entryBeforeUpdate.is_visible
      };

      updatedData[entryBeforeUpdateIndex] = updatedEntry;
      previousMap.set(siren, updatedData);
      const updatedMap = new Map(previousMap);
      return updatedMap;
    });
  };

  const getRows = accountingData => {
    const r = [];
    // eslint-disable-next-line complexity
    accountingData.forEach(entry => {
      const rowId = entry.idx;

      const entryLabel = `${entry.comptenum}${entry.comptelib ? ` - ${entry.comptelib}` : ''}`;
      const hiddenClassname = !entry.is_visible ? 'hidden-row-cell ' : '';

      if (hiddenRowsVisible || (!hiddenRowsVisible && entry.is_visible)) {
        r.push({
          rowId,
          height: 40,
          reorderable: true,
          cells: [
            {
              columnId: COLUMNS.compteLib.name,
              nonEditable: true,
              shouldDisplayDragIndicator: !!selectedRows.find(rId => rId === rowId),
              type: 'dragCell',
              text: entryLabel,
              className: `drag-cell ${hiddenClassname}`
            },
            {
              columnId: COLUMNS.mappingSig.name,
              type: 'text',
              text: entry.mapping_sig || '',
              className: hiddenClassname
            },
            {
              columnId: COLUMNS.mappingName.name,
              type: 'text',
              text: entry.mapping_name || '',
              className: hiddenClassname
            },
            {
              columnId: COLUMNS.mappingSubname.name,
              type: 'text',
              text: entry.mapping_subname || '',
              className: hiddenClassname
            },
            {
              columnId: COLUMNS.delete.name,
              type: 'toggleRowVisibility',
              isVisible: entry.is_visible,
              onToggle: () => toggleRowVisibility(rowId)
            }
          ]
        });
      }
    });
    return [HEADER_ROW, ...r];
  };

  const columns = getCustomMappingsColumns();
  const rows = getRows(data);

  const reorderArray = (arr, idxs, to) => {
    const movedElements = arr.filter((_, idx) => idxs.includes(idx));
    // eslint-disable-next-line no-param-reassign
    const targetIdx = Math.min(...idxs) < to ? (to += 1) : (to -= idxs.filter(idx => idx < to).length);
    const leftSide = arr.filter((_, idx) => idx < targetIdx && !idxs.includes(idx));
    const rightSide = arr.filter((_, idx) => idx >= targetIdx && !idxs.includes(idx));
    return [...leftSide, ...movedElements, ...rightSide];
  };

  const isTargetRowIdIsBetweenTwoCategories = (allRows, targetRowId, draggedRowIds) => {
    const toIndex = allRows.findIndex(r => r.idx === targetRowId);
    const draggedIndexes = draggedRowIds.map(id => allRows.findIndex(r => r.idx === id));

    const movingUp = Math.min(...draggedIndexes) > toIndex;

    // Get the position where the rows will be inserted
    const insertIndex = movingUp ? toIndex : toIndex + 1;

    const rowBefore = allRows[insertIndex - 1];
    const rowAfter = allRows[insertIndex];

    if (!rowBefore || !rowAfter) return false;

    setDroppedLinesCategories([rowBefore.mapping_sig, rowAfter.mapping_sig]);

    return rowBefore.mapping_sig !== rowAfter.mapping_sig;
  };

  const getUpdatedDataWithNewCategories = (baseData, targetRow, editedRowIds) => {
    const updatedDataWithNewCategories = baseData.map((entry, idx) => {
      if (editedRowIds.includes(idx)) {
        return {
          ...entry,
          mapping_sig: targetRow.mapping_sig,
          mapping_order: targetRow.mapping_order
        };
      }
      return {...entry};
    });
    return updatedDataWithNewCategories;
  };

  const handleRowsReorder = (targetRowId, rowIds) => {
    setData(previousMap => {
      const previousData = previousMap.get(siren);

      const toIndex = data.findIndex(entry => entry.idx === targetRowId);
      const rowsIds = rowIds.map(id => data.findIndex(entry => entry.idx === id));
      const toRow = previousData[toIndex];

      const isBetweenCategories = isTargetRowIdIsBetweenTwoCategories(previousData, targetRowId, rowsIds);
      if (isBetweenCategories) {
        setRowsIdsWaitingToBeMoved(rowsIds);
        setRowsToBeMovedTargetRowId(toIndex);
        setDroppedLinesMappingOrder(toRow.mapping_order);
        setChooseDroppedLinesCategoryModalOpen(true);
        return previousMap;
      }

      const updatedDataWithNewCategories = getUpdatedDataWithNewCategories(previousData, toRow, rowsIds);
      const updatedData = reorderArray(updatedDataWithNewCategories, rowsIds, toIndex);
      previousMap.set(siren, updatedData);
      return new Map(previousMap);
    });
  };

  const handleCanReorderRows = targetRowId => {
    return targetRowId !== 'header';
  };

  function doubleSortByOrderAndNumber(unsortedData) {
    // 1. Group the data by the "mapping_order" property
    const groupedByOrder = unsortedData.reduce((acc, item) => {
      const {mapping_order: mappingOrder, comptenum} = item;
      if (!acc[mappingOrder]) {
        acc[mappingOrder] = [];
      }
      acc[mappingOrder].push({...item});
      return acc;
    }, {});

    // 2. Sort each group by the "comptenum" property
    const sortedData = Object.values(groupedByOrder).flatMap(group => group.sort((a, b) => parseInt(a.comptenum, 10) - parseInt(b.comptenum, 10)));

    return sortedData;
  }

  // This hooks performs an initial ordering of the data
  useEffect(() => {
    setData(previousMap => {
      const unorderedData = previousMap.get(siren);
      const orderedData = doubleSortByOrderAndNumber(unorderedData);
      previousMap.set(siren, orderedData);
      const updatedMap = new Map(previousMap);
      return updatedMap;
    });
  }, []);

  const applyChangesToEntries = (changes, previousMap) => {
    const previousData = previousMap.get(siren);
    const updatedData = [...previousData.map(e => ({...e}))];

    // eslint-disable-next-line no-restricted-syntax
    for (const change of changes) {
      const {rowId, columnId} = change;

      const oldValue = change.previousCell.text;
      const newValue = change.newCell.text;

      const newValueIsEmpty = newValue === '';
      const columnRefusesEmptyValue = [COLUMNS.mappingSig.name, COLUMNS.mappingName.name].includes(columnId);

      if (newValueIsEmpty && columnRefusesEmptyValue) {
        break;
      }

      if (newValue !== oldValue) {
        const itemToUpdateIndex = updatedData.findIndex(item => item.idx === rowId);
        const updatedItem = updatedData[itemToUpdateIndex];
        updatedItem[columnId] = newValue;
        updatedData[itemToUpdateIndex] = updatedItem;
      }
    }

    previousMap.set(siren, updatedData);
    const updatedMap = new Map(previousMap);

    return updatedMap;
  };

  const handleChanges = changes => {
    setData(previousDataMap => applyChangesToEntries(changes, previousDataMap));
  };

  const handleDeleteMapping = (mappingKey, deletedMapping) => {
    setData(previousMap => {
      const previousData = previousMap.get(siren);
      const updatedData = [...previousData];

      const dataWithoutDeletedCategory = updatedData.map(entry => ({
        ...entry,
        [mappingKey]: entry[mappingKey] === deletedMapping ? '' : entry[mappingKey]
      }));

      previousMap.set(siren, dataWithoutDeletedCategory);
      const updatedMap = new Map(previousMap);

      return updatedMap;
    });
  };

  const handleContextMenu = (selectedRowIds, selectedColIds, selectionMode, menuOptions) => {
    if (selectedRowId === null) {
      return [];
    }

    let options = [...menuOptions];
    const currentRow = rows.find(r => r.rowId === selectedRowId);

    if (selectedColumnId === COLUMNS.mappingSig) {
      const selectedSig = currentRow?.cells[1]?.text;
      options = addContextMenuOption(options, 'deleteSigOption', 'Supprimer le SIG', () => handleDeleteMapping('mapping_sig', selectedSig));
    }

    if (selectedColumnId === COLUMNS.mappingName) {
      const selectedCategory = currentRow?.cells[2]?.text;
      options = addContextMenuOption(options, 'deleteCategoryOption', 'Supprimer la catégorie', () => handleDeleteMapping('mapping_name', selectedCategory));
    }

    if (selectedColumnId === COLUMNS.mappingSubname) {
      const selectedSubCategory = currentRow?.cells[3]?.text;
      options = addContextMenuOption(options, 'deleteSubcategoryOption', 'Supprimer la sous-catégorie', () => handleDeleteMapping('mapping_subname', selectedSubCategory));
    }

    return options;
  };

  // This hooks keeps track of which rows are currently selected
  useEffect(() => {
    const interval = setInterval(() => {
      if (gridRef.current) {
        const currentSelection = gridRef.current.state.selectedIds;
        setSelectedRows(currentSelection);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useLayoutEffect(() => {
    let animationFrameId = null;

    const updateShadow = () => {
      const shadow = document.querySelector('.rg-shadow');
      if (shadow) {
        shadow.style.setProperty('top', `${mouse.elementY}px`, 'important');
        shadow.style.setProperty('height', '40px', 'important');
      }

      animationFrameId = requestAnimationFrame(updateShadow);
    };

    updateShadow(); // Start the loop

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [mouse.elementY]);

  return (
    <Container chooseDroppedLinesCategoryModalOpen={chooseDroppedLinesCategoryModalOpen} ref={ref}>
      <BaseReactGrid
        id={SPREADSHEETS_IDS.CUSTOM_MAPPINGS}
        ref={gridRef}
        containerClassName="centered-spreadsheet"
        onFocusLocationChanged={handleCellFocusChange}
        onContextMenu={handleContextMenu}
        canReorderRows={handleCanReorderRows}
        onRowsReordered={handleRowsReorder}
        onCellsChanged={handleChanges}
        rows={rows}
        columns={columns}
        enableFillHandle
        enableRowSelection
        stickyTopRows={1}
        customCellTemplates={{
          toggleRowVisibility: new ToggleRowVisibilityCellTemplate(),
          dragCell: new TextCellWithDragIndicator()
        }}
      />
      <ChooseDroppedLinesCategoryModal
        open={chooseDroppedLinesCategoryModalOpen}
        onClose={() => setChooseDroppedLinesCategoryModalOpen(false)}
        selectedCategory={droppedLinesSelectedCategory}
        onCategorySelect={setDroppedLinesSelectedCategory}
        categories={droppedLinesCategories}
        onSubmit={() => {
          setData(previousMap => {
            const previousData = [...previousMap.get(siren)];
            const updatedDataWithNewCategories = getUpdatedDataWithNewCategories(
              previousData,
              {
                mapping_sig: droppedLinesSelectedCategory,
                mapping_order: droppedLinesMappingOrder
              },
              rowsIdsWaitingToBeMoved
            );
            const updatedData = reorderArray(updatedDataWithNewCategories, rowsIdsWaitingToBeMoved, rowsToBeMovedTargetRowId);
            previousMap.set(siren, updatedData);
            return new Map(previousMap);
          });

          setChooseDroppedLinesCategoryModalOpen(false);
          setDroppedLinesSelectedCategory(null);
          setRowsIdsWaitingToBeMoved([]);
          setRowsToBeMovedTargetRowId(null);
          setDroppedLinesMappingOrder(null);
        }}
      />
    </Container>
  );
};

CustomMappingsSpreadsheet.propTypes = {
  hiddenRowsVisible: bool.isRequired
};

export default memo(CustomMappingsSpreadsheet);
